import { ApplicationConfig, ErrorHandler, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withPreloading, PreloadAllModules } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors, HttpClient } from '@angular/common/http';
import { CoreModule } from './core/core.module';
import { jwtInterceptor } from './core/interceptors/jwt.interceptor';
import { csrfInterceptor } from './core/interceptors/csrf.interceptor';
import { GlobalErrorHandler } from './core/handlers/global-error.handler';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideState } from '@ngrx/store';
import { environment } from '../environments/environment';
import { uiReducer } from './core/store/ui/ui.reducer';
import { UiEffects } from './core/store/ui/ui.effects';
import { authReducer } from './core/store/auth/auth.reducer';
import { AuthEffects } from './core/store/auth/auth.effects';
import { donationsReducer } from './core/store/donations/donations.reducer';
import { DonationsEffects } from './core/store/donations/donations.effects';
import { studentsReducer } from './core/store/students/students.reducer';
import { StudentsEffects } from './core/store/students/students.effects';
import { expensesReducer } from './core/store/expenses/expenses.reducer';
import { ExpensesEffects } from './core/store/expenses/expenses.effects';
import { meetingsReducer } from './core/store/meetings/meetings.reducer';
import { MeetingsEffects } from './core/store/meetings/meetings.effects';
import { usersReducer } from './core/store/users/users.reducer';
import { UsersEffects } from './core/store/users/users.effects';
import { boardPortalReducer } from './core/store/board-portal/board-portal.reducer';
import { BoardPortalEffects } from './core/store/board-portal/board-portal.effects';
import { auditReducer } from './core/store/audit/audit.reducer';
import { AuditEffects } from './core/store/audit/audit.effects';
import { rolesReducer } from './core/store/roles/roles.reducer';
import { RolesEffects } from './core/store/roles/roles.effects';
import { campaignsReducer } from './core/store/campaigns/campaigns.reducer';
import { CampaignsEffects } from './core/store/campaigns/campaigns.effects';
import { donorsReducer } from './core/store/donors/donors.reducer';
import { DonorsEffects } from './core/store/donors/donors.effects';
import { securityReducer } from './core/store/security/security.reducer';
import { SecurityEffects } from './core/store/security/security.effects';
import { annualReceiptsReducer } from './core/store/annual-receipts/annual-receipts.reducer';
import { AnnualReceiptsEffects } from './core/store/annual-receipts/annual-receipts.effects';

import { routes } from './app.routes';

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

/**
 * Root configuration of the standalone application.
 *
 * CoreModule se registra aqui via importProvidersFrom como equivalente
 * a importarlo en el AppModule tradicional. El guard del CoreModule
 * prevents any accidental duplicate import.
 *
 * PreloadAllModules preloads domain modules in the background
 * tras el inicio de la aplicacion para mejorar la experiencia de navegacion.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    provideAnimations(),
    provideHttpClient(withInterceptors([jwtInterceptor, csrfInterceptor])),
    provideStore(),
    provideState('ui', uiReducer),
    provideState('auth', authReducer),
    provideState('donations', donationsReducer),
    provideState('students', studentsReducer),
    provideState('expenses', expensesReducer),
    provideState('meetings', meetingsReducer),
    provideState('users', usersReducer),
    provideState('boardPortal', boardPortalReducer),
    provideState('audit', auditReducer),
    provideState('roles', rolesReducer),
    provideState('campaigns', campaignsReducer),
    provideState('donors', donorsReducer),
    provideState('security', securityReducer),
    provideState('annualReceipts', annualReceiptsReducer),
    provideEffects(UiEffects, AuthEffects, DonationsEffects, StudentsEffects, ExpensesEffects, MeetingsEffects, UsersEffects, BoardPortalEffects, AuditEffects, RolesEffects, CampaignsEffects, DonorsEffects, SecurityEffects, AnnualReceiptsEffects),
    provideStoreDevtools({ logOnly: environment.production, connectInZone: true }),
    importProvidersFrom(
      CoreModule,
      TranslateModule.forRoot({
        defaultLanguage: 'es',
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      })
    ),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
  ]
};
